import { Link } from "@mui/material";
import { useSignal, useSignalEffect } from "@preact/signals-react";
import { publishUserAction } from "../../library/analytics";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import { INGLinkProps } from "../../library/NGFieldExtensions";
import {
  getClassName,
  getFirstNonEmpty,
  getTestId,
  getsxObject,
  normalizeSessionUrl,
  partitionMUIClasses,
} from "../../library/utils";
import { validateCookiesForUrl } from "../../library/cookieValidation";
import { log } from "../../library/logger";

const tag = "NGLink";

export default function NGLink({ config, context }: INGLinkProps) {
  const local = setupLocalState(
    config,
    {
      Label: useSignal(config.Label ?? ""),
      URL: useSignal(config.URL ?? ""),
      Title: useSignal(config.Title ?? ""),
      Target: useSignal(config.Target ?? ""),
      Visible: useSignal(config.Visible != false ? true : config.Visible),
      Style: useSignal(config.Style ?? {}),
      Classes: useSignal(config.Classes ?? []),
    },
    context
  );

  const handlers = setupHandlers(config, context);

  // Validate cookies for the link URL before rendering
  const cookieValidated = useSignal(false);

  useSignalEffect(() => {
    const validateLink = async () => {
      if (!local.URL.value) {
        cookieValidated.value = true;
        return;
      }

      try {
        await validateCookiesForUrl(normalizeSessionUrl(local.URL.value));
        cookieValidated.value = true;
      } catch (error) {
        log.error(tag, "Cookie validation failed for link:", local.URL.value, error);
        cookieValidated.value = false;
      }
    };

    validateLink();
  });

  return (
    <>
      {local.Visible.value && cookieValidated.value && (
        <Link
          data-testid={getTestId(config)}
          data-type={config.__typename}
          target={local.Target.value}
          href={normalizeSessionUrl(local.URL.value)}
          sx={getsxObject(local.Style.value)}
          className={getClassName(local.Classes, context)}
          title={local.Title.value}
          {...handlers}
          onClick={(e) => {
            const [_, classes] = partitionMUIClasses(local.Classes.value);
            const action = publishUserAction(config, context, "link_click", e, {
              EventCategory: "engagement",
              LinkTarget: local.Target.value,
              LinkUrl: local.URL.value,
              Outbound: !!local.URL.value?.startsWith("http"),
              ElementText: getFirstNonEmpty(local.Label.value, local.Title.value),
              ElementClass: classes.join(" "),
            });
            handlers["onClick"]?.(e, null, action);
          }}
        >
          {local.Label.value}
        </Link>
      )}
    </>
  );
}
